import type {
  BomDocument,
  HardwareRow,
  HardwareTable,
  WoodRow,
} from '@/lib/bom'

function dash(value: string): string {
  return value?.trim() ? value : '—'
}

function woodTone(type: string): string {
  const n = type.toLowerCase()
  if (n.includes('mdf')) return 'bg-blue-50 text-blue-700'
  if (n.includes('plywood')) return 'bg-purple-50 text-purple-700'
  if (n.includes('cao su') || n.includes('tràm') || n.includes('tram')) {
    return 'bg-amber-50 text-amber-800'
  }
  return 'bg-slate-100 text-slate-700'
}

function WoodTable({ doc }: { doc: BomDocument }) {
  const heading =
    doc.woodTitle ||
    (doc.productCode
      ? `Định mức nguyên vật liệu gỗ - ${doc.productCode}`
      : 'Định mức nguyên vật liệu gỗ')
  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-3.5 text-white">
        <div className="flex items-center space-x-2">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          <h3 className="text-xs font-bold tracking-wider uppercase">{heading}</h3>
        </div>
        <span className="rounded-full border border-emerald-300 bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
          Chỉ xem
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left text-xs text-slate-700">
          <thead>
            <tr className="border-b border-slate-300 bg-slate-100 text-center text-xs font-semibold tracking-wider text-slate-800 uppercase">
              <th className="w-10 border-r border-slate-200 p-2.5" rowSpan={2}>
                STT
              </th>
              <th className="min-w-[240px] border-r border-slate-200 p-2.5 text-left" rowSpan={2}>
                Tên Chi Tiết Sản Phẩm
              </th>
              <th className="min-w-[90px] border-r border-slate-200 p-2.5" rowSpan={2}>
                Loại Gỗ / Phôi
              </th>
              <th className="border-r border-slate-200 bg-blue-50/70 p-2 text-blue-950" colSpan={4}>
                Kích Thước Tinh (mm)
              </th>
              <th className="border-r border-slate-200 bg-emerald-50/70 p-2 text-emerald-950" colSpan={2}>
                Số Lượng
              </th>
              <th className="min-w-[100px] border-r border-slate-200 p-2.5" rowSpan={2}>
                Ghi Chú Kỹ Thuật
              </th>
              <th className="min-w-[130px] border-r border-slate-200 p-2.5" rowSpan={2}>
                Quy Cách Veneer
              </th>
              <th className="min-w-[90px] bg-slate-200 p-2.5 text-right text-slate-900" rowSpan={2}>
                Khối Lượng Thể Tích (m³)
              </th>
            </tr>
            <tr className="border-b border-slate-300 bg-slate-50 text-center text-xs font-medium text-slate-600">
              <th className="border-r border-slate-200 bg-blue-50/30 px-2 py-1.5">Dày</th>
              <th className="border-r border-slate-200 bg-blue-50/30 px-2 py-1.5">Rộng</th>
              <th className="border-r border-slate-200 bg-blue-50/30 px-2 py-1.5">Dài</th>
              <th className="border-r border-slate-200 bg-blue-50/30 px-2 py-1.5 font-semibold text-blue-900">
                Mộng (Tenon)
              </th>
              <th className="border-r border-slate-200 bg-emerald-50/30 px-2 py-1.5 font-bold text-emerald-800">
                SL (1 SP)
              </th>
              <th className="border-r border-slate-200 bg-emerald-50/30 px-2 py-1.5 font-bold text-emerald-800">
                SL Tổng
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 font-mono text-xs">
            {doc.wood.map((row) => (
              <WoodLine key={`${row.stt}-${row.name}`} row={row} />
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-300 bg-amber-100/70 font-bold text-slate-900">
              <td
                className="border-r border-slate-300 p-2.5 text-right font-sans text-xs tracking-wide uppercase"
                colSpan={7}
              >
                Tổng cộng khối lượng cho 01 sản phẩm:
              </td>
              <td className="border-r border-slate-300 p-2 text-center font-mono text-xs text-emerald-800">
                {doc.woodPartCount} Chi tiết
              </td>
              <td className="border-r border-slate-300 p-2 text-center font-mono text-xs text-emerald-800">
                {doc.woodPartCount} Chi tiết
              </td>
              <td
                className="border-r border-slate-300 bg-amber-200/50 p-2 text-center font-sans text-xs"
                colSpan={2}
              >
                TỔNG M³ (CBM)
              </td>
              <td className="bg-amber-200/70 p-2.5 text-right font-mono text-xs font-bold text-emerald-800">
                {doc.woodTotalM3.toFixed(3)} m³
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  )
}

function WoodLine({ row }: { row: WoodRow }) {
  return (
    <tr className="transition-colors hover:bg-slate-50">
      <td className="border-r border-slate-200 p-2 text-center font-sans text-slate-500">
        {row.stt}
      </td>
      <td className="border-r border-slate-200 p-2 font-sans font-semibold text-slate-900">
        {row.name}
      </td>
      <td className="border-r border-slate-200 p-2 text-center font-sans">
        {row.woodType ? (
          <span className={`rounded px-2 py-0.5 text-xs font-medium ${woodTone(row.woodType)}`}>
            {row.woodType}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className="border-r border-slate-200 p-2 text-center font-bold">{dash(row.thick)}</td>
      <td className="border-r border-slate-200 p-2 text-center">{dash(row.width)}</td>
      <td className="border-r border-slate-200 p-2 text-center">{dash(row.length)}</td>
      <td
        className={`border-r border-slate-200 p-2 text-center font-sans ${row.tenon ? 'bg-blue-50/20 font-semibold text-blue-800' : 'text-slate-400'}`}
      >
        {dash(row.tenon)}
      </td>
      <td className="border-r border-slate-200 p-2 text-center font-bold text-slate-800">
        {dash(row.qty)}
      </td>
      <td className="border-r border-slate-200 bg-emerald-50/40 p-2 text-center font-bold text-emerald-800">
        {dash(row.qtyTotal)}
      </td>
      <td className="border-r border-slate-200 p-2 font-sans text-slate-600">{dash(row.note)}</td>
      <td className="border-r border-slate-200 p-2 font-sans text-slate-700">{dash(row.veneer)}</td>
      <td className="bg-slate-50/80 p-2 text-right font-bold text-slate-900">{dash(row.m3)}</td>
    </tr>
  )
}

function HardwareTableView({ table }: { table: HardwareTable }) {
  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-3.5 text-white">
        <div className="flex items-center space-x-2">
          <span className="h-2.5 w-2.5 rounded-full bg-cyan-400" />
          <h3 className="text-xs font-bold tracking-wider uppercase">{table.title}</h3>
        </div>
        <span className="rounded-full border border-cyan-300 bg-cyan-100 px-2.5 py-0.5 text-xs font-semibold text-cyan-900">
          Chỉ xem
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left text-xs text-slate-700">
          <thead>
            <tr className="border-b border-slate-300 bg-slate-100 text-center text-xs font-semibold tracking-wider text-slate-800 uppercase">
              <th className="w-10 border-r border-slate-200 p-2.5">STT</th>
              <th className="min-w-[200px] border-r border-slate-200 p-2.5 text-left">
                Tên Vật Tư / Quy Chuẩn Kim Khí
              </th>
              <th className="min-w-[140px] border-r border-slate-200 p-2.5">Quy Cách Kỹ Thuật</th>
              <th className="w-24 border-r border-slate-200 bg-emerald-50/50 p-2.5 text-emerald-950">
                Số Lượng
              </th>
              <th className="w-24 border-r border-slate-200 bg-emerald-50/50 p-2.5 text-emerald-950">
                SL Tổng Lô
              </th>
              <th className="w-20 border-r border-slate-200 p-2.5">Đơn Vị</th>
              <th className="w-28 border-r border-slate-200 p-2.5">Màu Sắc</th>
              <th className="w-24 border-r border-slate-200 p-2.5">Phân Loại</th>
              <th className="min-w-[180px] p-2.5 text-left">Vị Trí Lắp Đặt / Ghi Chú</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 font-mono text-xs">
            {table.rows.map((row) => (
              <HardwareLine key={`${table.title}-${row.stt}-${row.name}-${row.spec}`} row={row} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function HardwareLine({ row }: { row: HardwareRow }) {
  const highlight = /ray|tay nắm/i.test(row.name)
  return (
    <tr className={`transition-colors hover:bg-slate-50 ${highlight ? 'bg-cyan-50/20' : ''}`}>
      <td className="border-r border-slate-200 p-2 text-center font-sans text-slate-500">
        {row.stt}
      </td>
      <td
        className={`border-r border-slate-200 p-2 font-sans font-semibold ${highlight ? 'text-blue-900' : 'text-slate-800'}`}
      >
        {row.name}
      </td>
      <td className="border-r border-slate-200 p-2 text-center font-bold text-slate-700">
        {dash(row.spec)}
      </td>
      <td className="border-r border-slate-200 p-2 text-center font-bold text-slate-900">
        {dash(row.qty)}
      </td>
      <td className="border-r border-slate-200 bg-emerald-50/40 p-2 text-center font-bold text-emerald-800">
        {dash(row.qtyTotal)}
      </td>
      <td className="border-r border-slate-200 p-2 text-center font-sans">
        {row.unit ? (
          <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
            {row.unit}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className="border-r border-slate-200 p-2 text-center font-sans font-medium text-amber-700">
        {dash(row.color)}
      </td>
      <td className="border-r border-slate-200 p-2 text-center font-sans">
        {row.kind ? (
          <span className="rounded bg-slate-200 px-2 py-0.5 text-xs text-slate-800">
            {row.kind}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className="p-2 font-sans text-slate-600">{dash(row.note)}</td>
    </tr>
  )
}

export function BomTables({ doc }: { doc: BomDocument }) {
  const tables =
    doc.hardwareTables?.length > 0
      ? doc.hardwareTables
      : doc.hardware.length
        ? [
            {
              title: doc.productCode
                ? `VẬT TƯ SẢN XUẤT- ${doc.productCode}`
                : 'VẬT TƯ SẢN XUẤT',
              code: doc.productCode,
              rows: doc.hardware,
            } satisfies HardwareTable,
          ]
        : []

  return (
    <div className="space-y-4 bg-slate-100 p-4">
      <div className="px-1">
        <h2 className="text-base font-bold tracking-tight text-slate-900 uppercase">
          Thông tin sản phẩm &amp; định mức nguyên vật liệu gỗ (BOM GỖ)
        </h2>
        {doc.title ? <p className="mt-1 text-xs text-slate-500">{doc.title}</p> : null}
      </div>
      {doc.wood.length ? <WoodTable doc={doc} /> : null}

      {tables.length ? (
        <>
          <div className="px-1 pt-2">
            <h2 className="text-base font-bold tracking-tight text-slate-900 uppercase">
              Vật tư phụ, phụ kiện &amp; ngũ kim sản xuất (BOM NGŨ KIM)
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              {tables.length} bảng ngũ kim — title lấy đúng công thức Excel (&amp; ô mã)
            </p>
          </div>
          {tables.map((table, index) => (
            <HardwareTableView key={`${table.title}-${index}`} table={table} />
          ))}
        </>
      ) : null}
    </div>
  )
}
